import { useState, useCallback, useEffect, useRef } from 'react'
import { Gauge, LayoutDashboard, Building2, Tags, Activity, SlidersHorizontal, LayoutGrid, X } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { platformCan, type PlatformArea, type PlatformCapabilities } from '@papan/types'
import { SidebarItem, SidebarSection, ScrollSpy } from './settingsChrome'
import { useSwapScroll } from './swap-scroll'

/** A destination whose render prop the caller did not supply. */
function PageUnavailable() {
  const { t } = useTranslation('core-ui')
  return (
    <div className="p-4 themed-text-muted text-sm">
      {t('settings.platform.unavailable', { defaultValue: 'This page is not available.' })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Usage & Billing — the PLATFORM shelf. Cross-tenant controls only: the ceiling
// imposed on every tenant, what each metered feature costs, and the plan/pack
// catalog. Launched from its own taskbar (shelf) button.
//
// Same layout scaffold as SettingsView (header + sidebar + content +
// scrollspy); the pages arrive as render props because the budget client +
// billing UI live in features/, which @papan/core-ui may not import.
//
// ── Why there is nothing org-scoped here ────────────────────────────────
// This surface used to carry BOTH scopes behind a switch: a tenant's own
// budget/limits/subscription alongside the platform ceiling. That put "raise my
// org's cap" and "raise every tenant's ceiling" in one sidebar, one mis-click
// apart, and the mis-click is cross-tenant. The switch made the blast radius
// visible but did not remove it.
//
// A tenant's own spend is now ORG CONFIGURATION and lives with the rest of it,
// in Settings → Organization (Spending, Budget, Billing) — reachable by
// whoever holds the capability, which is how every other org setting already
// works. What is left here is only the cross-tenant tier, so the shelf itself
// is the boundary: if you can open it, you are administering the platform.
// That is also what the server says since the platform-admin scoping change —
// `requirePlatformAdmin` needs both the role AND acting in the platform org.
// ---------------------------------------------------------------------------

// NOTE: no 'usage' page, and no org pages. A member's OWN spend is Settings →
// Usage; an org's own budget is Settings → Organization → Budget.
//
// ── The six destinations (redesign phase 1 + launcher) ──────────────────
// Reorganised from three mechanism-shaped screens (Tenant Budgets / Feature
// Pricing / Catalog) to five audience-shaped ones. The principle: THE CUSTOMER
// IS THE OBJECT, and everything else is either the price book, the platform's
// own configuration, or an operations queue. Configuring one paying customer
// used to mean visiting three screens and finding them in a picker twice.
// `launcher` (which apps a tenant sees) joins them as a sixth, cross-tenant
// like the rest.
//
// These names are also the CAPABILITY AREAS (`PlatformArea`), which is not a
// coincidence — the nav is the access boundary made visible. `pricebook` maps
// to the `pricing` area; `launcher` (per-tenant app curation) to `customers`;
// the rest match one-to-one.
export type BudgetBillingPage =
  | 'overview'
  | 'customers'
  | 'pricebook'
  | 'operations'
  | 'settings'
  | 'launcher'

/**
 * Which capability area each destination reads from.
 *
 * Kept beside the page list rather than inferred from the name, because
 * `pricebook` → `pricing` would otherwise be a silent string mismatch that
 * renders a nav item nobody can use. `launcher` has no area of its own — its
 * routes carry blanket `requirePlatformAdmin`, so it rides `customers` (the
 * closest audience — it curates a specific tenant's apps) for nav visibility.
 */
export const PAGE_AREA: Record<BudgetBillingPage, PlatformArea> = {
  overview: 'operations',
  customers: 'customers',
  pricebook: 'pricing',
  operations: 'operations',
  settings: 'settings',
  launcher: 'customers',
}

/**
 * Path segment(s) under `/billing/` for each page.
 *
 * Every page still nests under `platform/` so the SCOPE IS IN THE LINK. A
 * pasted URL, a bookmark or a support ticket then says which set of controls it
 * opens without anyone having to know that (say) `pricing` is cross-tenant.
 *
 * The prefix is kept even though the whole shelf is now platform-only: it means
 * every URL minted before this reorganisation still carries a segment the
 * parser recognises, so `LEGACY_PAGE_PATH` below is a rename map rather than a
 * rescue mission.
 */
const PAGE_PATH: Record<BudgetBillingPage, string> = {
  overview: 'platform/overview',
  customers: 'platform/customers',
  pricebook: 'platform/pricebook',
  operations: 'platform/operations',
  settings: 'platform/settings',
  launcher: 'platform/launcher',
}

/**
 * Sub-page segments that existed before phase 1, and where they now land.
 *
 * These are live in bookmarks and support tickets. They must not 404 and — more
 * importantly — must not fall through to the default destination, because that
 * would silently open Overview on someone who pasted a link to a specific
 * tenant's caps and read it as "the link is broken".
 *
 * `tenants` → `customers` and `catalog` → `pricebook` are pure renames.
 * `pricing` → `pricebook` is a MERGE: feature pricing and the catalog were two
 * screens and are now one (the merge of their CONTENT lands in phase 4; the
 * destination is unified now so the URL never has to change twice).
 */
const LEGACY_PAGE_PATH: Record<string, BudgetBillingPage> = {
  tenants: 'customers',
  pricing: 'pricebook',
  catalog: 'pricebook',
}

/** The path a page lives at, relative to `<prefix>/billing/`. */
export function budgetBillingPagePath(page: BudgetBillingPage): string {
  return PAGE_PATH[page]
}

/**
 * A Settings page a former `/billing/*` URL now lives at.
 *
 * These paths are linkable, bookmarked and pasted into support tickets, so
 * moving the org pages out means the old URLs must land somewhere correct
 * rather than on an empty shelf. Callers redirect to `<prefix>/settings/<key>`.
 */
export type BudgetBillingSettingsRedirect = 'usage' | 'budget' | 'limits' | 'subscription'

export interface BudgetBillingRoute {
  /** Page to open; undefined when the URL names none (bare `/billing`) or names
   *  one this surface does not own. Callers fall back to their default. */
  page?: BudgetBillingPage
  /** Set when the URL names a page that MOVED to Settings. Callers redirect to
   *  `<prefix>/settings/<settingsPage>` instead of rendering this surface. */
  settingsPage?: BudgetBillingSettingsRedirect
  /**
   * The tenant a `customers/<id>` URL names — the customer detail page.
   *
   * Carried on the route rather than held as component state so a detail page
   * is LINKABLE: "look at this customer" is the single most likely thing an
   * operator pastes into a ticket or a chat, and a modal or a state-only drill
   * -down would make that impossible. It also means the browser back button
   * returns to the list, which is what a list-detail pair has to do.
   */
  tenantId?: string
  /**
   * The plan a `pricebook/<planId>` URL names — the per-plan editor.
   *
   * Same reasoning as `tenantId` above, one destination over. The editor is
   * four sub-editors and roughly 1,200px of controls — a screen's worth of one
   * subject — and it previously rendered as a panel BELOW the whole plans
   * table, so the control and its editor were separated by however many rows
   * followed, and the distance grew with the catalogue.
   */
  planId?: string
}

/**
 * Parse a location pathname into the page this surface should open.
 *
 * Lives here rather than in each shell because App.tsx and MobileShell both
 * route to this component and previously disagreed about how: the desktop
 * parsed `/billing/:page`, while mobile passed its TAB name ('drive') as the
 * active page, so every mobile deep link silently opened the dashboard.
 *
 * Accepts the pre-scope paths (`/billing/platform`, `/billing/pricing`) so
 * existing links and bookmarks keep resolving, and maps the four org paths onto
 * their new Settings homes rather than dropping them.
 */
export function parseBudgetBillingPath(pathname: string): BudgetBillingRoute {
  const m = pathname.match(/\/billing(?:\/([^/]+))?(?:\/([^/]+))?(?:\/([^/]+))?/)
  const first = m?.[1]
  const second = m?.[2]
  const third = m?.[3]
  if (!first) return {}
  // `platform/customers/<tenantId>` — the customer detail page. Checked before
  // the page lookup below because that lookup matches on the two-segment path
  // and would otherwise drop the id, silently opening the LIST for a link that
  // named one customer.
  if (first === 'platform' && second === 'customers' && third) {
    return { page: 'customers', tenantId: third }
  }
  // `platform/pricebook/<planId>` — one plan's editor. Checked here for the
  // same reason as the line above: the two-segment lookup below would match
  // `platform/pricebook` and silently drop the id, opening the LIST for a link
  // that named one plan.
  if (first === 'platform' && second === 'pricebook' && third) {
    return { page: 'pricebook', planId: third }
  }
  if (first === 'platform') {
    // `/billing/platform` alone (the pre-scope tenant-budgets path) lands on
    // Customers, which is what that URL meant. Anything under `platform/` must
    // be a current page or a known former one — an unrecognized segment falls
    // back rather than rendering an empty pane.
    if (!second) return { page: 'customers' }
    const current = (Object.keys(PAGE_PATH) as BudgetBillingPage[])
      .find((p) => PAGE_PATH[p] === `platform/${second}`)
    if (current) return { page: current }
    const renamed = LEGACY_PAGE_PATH[second]
    if (renamed) return { page: renamed }
    // Unknown sub-page. Customers rather than Overview: an unrecognized
    // `platform/*` link is far more likely to be a stale deep link to a
    // specific tenant than to the dashboard, so this lands nearer where the
    // reader was going.
    return { page: 'customers' }
  }
  // Legacy FLAT path for the pre-scope pricing page (`/billing/pricing`).
  if (first === 'pricing') return { page: 'pricebook' }
  // Moved to Settings. `usage` went there when Tier-3 moved out; the other
  // three followed when this shelf became platform-only.
  const moved = (['usage', 'budget', 'limits', 'subscription'] as const).find((p) => p === first)
  return moved ? { settingsPage: moved } : {}
}

interface BudgetBillingViewProps {
  activePage?: BudgetBillingPage
  /** Typed, not `string`: the caller must map the key through
   *  `budgetBillingPagePath` to build the URL, because every page nests under a
   *  `platform/` segment and concatenating the raw key emits a path this view's
   *  own parser would reject. */
  onPageChange?: (page: BudgetBillingPage) => void
  /** The six destinations (render props — same shared/* → features/* boundary
   *  reason as SettingsView's page props). */
  overviewPage?: React.ReactNode
  customersPage?: React.ReactNode
  pricebookPage?: React.ReactNode
  operationsPage?: React.ReactNode
  settingsPage?: React.ReactNode
  /** Which apps appear on a tenant's launcher. Cross-tenant like the rest of
   *  the shelf, and — see LauncherAdmin's header — it inherits the shelf's
   *  undifferentiated grant along with everything else here. */
  launcherPage?: React.ReactNode
  /**
   * Which areas this viewer may READ, for nav visibility only.
   *
   * Optional, and an omitted value shows everything: the server refuses what
   * the caller may not do regardless, so a caller that has not wired this up
   * gets the pre-phase-0 behaviour rather than an empty sidebar. Enforcement is
   * `requirePlatformCapability`; this only avoids offering a locked door.
   */
  capabilities?: PlatformCapabilities
  /** Phone viewport: sidebar becomes a horizontal chip strip. */
  mobile?: boolean
  /** Mobile close (desktop closes via its deck window). */
  onClose?: () => void
}

/** Nav order. Overview first because it answers "is anything wrong". */
const NAV: ReadonlyArray<{ page: BudgetBillingPage; icon: typeof Gauge; labelKey: string }> = [
  { page: 'overview', icon: LayoutDashboard, labelKey: 'settings.platform.nav.overview' },
  { page: 'customers', icon: Building2, labelKey: 'settings.platform.nav.customers' },
  { page: 'pricebook', icon: Tags, labelKey: 'settings.platform.nav.pricebook' },
  { page: 'operations', icon: Activity, labelKey: 'settings.platform.nav.operations' },
  { page: 'launcher', icon: LayoutGrid, labelKey: 'launcher.admin.navLabel' },
  { page: 'settings', icon: SlidersHorizontal, labelKey: 'settings.platform.nav.settings' },
]

/**
 * No `user` / `tenantId` / `orgEmail` props any more, and no gating inside.
 *
 * Every page here is Tier-1, so "may this viewer see a nav item" and "may this
 * viewer open this surface at all" became the same question — and the caller
 * already answers it, via `useBudgetBillingAccess`, to decide whether to render
 * the shelf button and the route. Re-deriving it here would be a second copy of
 * one access rule, which is exactly how the two drift apart.
 *
 * None of this is access control regardless: `requirePlatformAdmin` is, and it
 * needs both the platform role AND acting in the platform org. This only avoids
 * offering a door that is locked.
 */
export function BudgetBillingView({
  activePage,
  onPageChange,
  overviewPage,
  customersPage,
  pricebookPage,
  operationsPage,
  settingsPage,
  launcherPage,
  capabilities,
  mobile,
  onClose,
}: BudgetBillingViewProps) {
  const { t } = useTranslation('core-ui')

  const [currentPage, setCurrentPage] = useState<BudgetBillingPage>(activePage ?? 'overview')

  useEffect(() => {
    if (activePage) setCurrentPage(activePage)
  }, [activePage])

  const navigateTo = useCallback((page: BudgetBillingPage) => {
    setCurrentPage(page)
    onPageChange?.(page)
  }, [onPageChange])

  const contentRef = useRef<HTMLDivElement>(null)
  // A page switch opens at the top; not on mount — a layout swap puts the
  // view back where the user was (#897).
  const scrolledPage = useRef(currentPage)
  useEffect(() => {
    if (scrolledPage.current === currentPage) return
    scrolledPage.current = currentPage
    contentRef.current?.scrollTo({ top: 0 })
  }, [currentPage])
  useSwapScroll(`billing:${currentPage}`, contentRef)

  // Mouse / pen drag-to-scroll + wheel → horizontal for the mobile nav strip.
  const navRef = useRef<HTMLElement>(null)
  const navDrag = useRef<{ x: number; scroll: number; moved: boolean; id: number } | null>(null)
  const navSuppressClick = useRef(false)
  const onNavPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === 'touch' || !navRef.current) return
    navSuppressClick.current = false
    navDrag.current = { x: e.clientX, scroll: navRef.current.scrollLeft, moved: false, id: e.pointerId }
  }
  const onNavPointerMove = (e: React.PointerEvent) => {
    const d = navDrag.current
    const el = navRef.current
    if (!d || !el) return
    const dx = e.clientX - d.x
    if (!d.moved) {
      if (Math.abs(dx) <= 4) return
      d.moved = true
      try { el.setPointerCapture(d.id) } catch { /* not capturable */ }
    }
    el.scrollLeft = d.scroll - dx
  }
  const onNavPointerUp = () => {
    const d = navDrag.current
    const el = navRef.current
    if (!d || !el) return
    navDrag.current = null
    try { el.releasePointerCapture(d.id) } catch { /* already released */ }
    if (d.moved) navSuppressClick.current = true
  }
  const onNavWheel = (e: React.WheelEvent) => {
    const el = navRef.current
    if (!el) return
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) el.scrollLeft += e.deltaY
  }

  const renderPage = () => {
    switch (currentPage) {
      case 'overview':
        return overviewPage || <PageUnavailable />
      case 'pricebook':
        return pricebookPage || <PageUnavailable />
      case 'operations':
        return operationsPage || <PageUnavailable />
      case 'settings':
        return settingsPage || <PageUnavailable />
      case 'launcher':
        return launcherPage || <PageUnavailable />
      case 'customers':
      default:
        return customersPage || <PageUnavailable />
    }
  }

  // Nav visibility. An omitted `capabilities` shows everything — see the prop
  // docs; this is not the enforcement point.
  const visible = NAV.filter(
    (n) => !capabilities || platformCan(capabilities, PAGE_AREA[n.page], 'read'),
  )

  return (
    <div className="flex-1 min-h-0 min-w-0 themed-bg-primary flex flex-col">
      {/* Header */}
      <div className="h-12 border-b themed-border flex items-center px-4">
        <Gauge size={20} className="themed-icon mr-2" />
        <span className="themed-text font-medium">{t('taskBar.platform')}</span>
        {mobile && onClose && (
          <>
            <div className="flex-1" />
            <button
              onClick={onClose}
              aria-label="Close"
              title="Close"
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded transition-colors themed-text-muted hover:themed-bg-tertiary hover:themed-text"
            >
              <X size={14} />
            </button>
          </>
        )}
      </div>

      {/* Body: same centered sidebar + content + scrollspy scaffold as
          SettingsView. The OUTER div is the one scroll container. */}
      <div
        ref={contentRef}
        className="flex-1 min-h-0 overflow-y-auto [container-type:size]"
      >
        {/* ── The width budget ────────────────────────────────────────────
            `max-w-7xl` (1280) was the ONLY cap that ever bound. Measured: the
            content column maxed at 828px however wide the window, because
            1280 − 48 padding − 180 nav − 160 scroll-spy − 64 gaps = 828, and
            the pages' own `max-w-5xl` (1024) therefore never bound at all.

            828px could not hold the plans table (796px natural) once a third
            plan existed, so the console scrolled sideways on a 1512px screen
            with ~590px of it unused. Raised so a comparison grid gets room;
            PROSE is capped per-paragraph instead (see `Section`), because a
            1,100px line of body text is its own readability problem. */}
        <div className={`flex w-full mx-auto ${mobile ? 'max-w-5xl flex-col px-3 py-3 gap-3' : 'max-w-[1600px] px-6 gap-8'}`}>
          <nav
            ref={mobile ? navRef : undefined}
          // #897: chrome, not content — and on the phone a sticky header the
          // restored position sits below.
          data-scroll-skip
          data-scroll-sticky={mobile ? '' : undefined}
            onPointerDown={mobile ? onNavPointerDown : undefined}
            onPointerMove={mobile ? onNavPointerMove : undefined}
            onPointerUp={mobile ? onNavPointerUp : undefined}
            onPointerCancel={mobile ? onNavPointerUp : undefined}
            onWheel={mobile ? onNavWheel : undefined}
            onClickCapture={mobile ? (e) => { if (navSuppressClick.current) { e.stopPropagation(); navSuppressClick.current = false } } : undefined}
            className={mobile
              ? 'flex shrink-0 items-center gap-1.5 overflow-x-auto pb-1 cursor-grab active:cursor-grabbing [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sticky top-0 z-10 themed-bg-primary'
              : 'w-[180px] shrink-0 py-7 sticky top-0 self-start max-h-[100cqh] overflow-y-auto'}>
            {/* The section label is kept even though the shelf is now wholly
                platform-scoped: it is the last thing read before a control that
                edits OTHER people's tenants, and every item below it is one. */}
            <SidebarSection label={t('settings.budget.scope.platform')} mobile={mobile} />
            {visible.map((n) => (
              <SidebarItem
                key={n.page}
                icon={n.icon}
                label={t(n.labelKey)}
                active={currentPage === n.page}
                onClick={() => navigateTo(n.page)}
                mobile={mobile}
              />
            ))}
          </nav>

          <div className={`flex-1 min-w-0 ${mobile ? '' : 'py-6'}`}>
            {renderPage()}
          </div>

          {/* Own id namespace — the Settings deck stays mounted (hidden) beside
              this one, and a shared prefix collided on every heading index. */}
          {!mobile && <ScrollSpy containerRef={contentRef} pageKey={currentPage} idPrefix="billing-spy" />}
        </div>
      </div>
    </div>
  )
}
